import type { TextNode } from '@repo/api-contracts';
import type { MeasureText } from '../types';
import { REFERENCE_FONT_PX, TEXT_ASCENT_EM } from './text-style';

export interface TextLayoutLine {
  readonly text: string;
  readonly widthMm: number;
  readonly xMm: number;
  /** 줄 기준선의 y(mm, 텍스트 상자 위 기준). */
  readonly baselineMm: number;
}

export interface TextLayout {
  readonly lines: readonly TextLayoutLine[];
  readonly heightMm: number;
  readonly lineHeightMm: number;
}

type LayoutInput = Pick<
  TextNode,
  'text' | 'width' | 'fontSizeMm' | 'lineHeight' | 'letterSpacingEm' | 'align'
>;

/** 글자 하나의 전진 폭(mm) — 100px 측정값을 글자 크기로 환산한다. */
const advanceMm = (
  char: string,
  familyName: string,
  fontSizeMm: number,
  measure: MeasureText,
): number => (measure(char, familyName) / REFERENCE_FONT_PX) * fontSizeMm;

/** 문자열 폭(mm) — 글자 전진 폭 합 + 글자 사이 자간. */
const lineWidthMm = (
  chars: readonly string[],
  node: LayoutInput,
  familyName: string,
  measure: MeasureText,
): number => {
  if (chars.length === 0) return 0;
  const advances = chars.reduce(
    (sum, char) => sum + advanceMm(char, familyName, node.fontSizeMm, measure),
    0,
  );
  return advances + (chars.length - 1) * node.letterSpacingEm * node.fontSizeMm;
};

/** 공백을 앞 단어에 붙여 토큰으로 나눈다 — '가나 다' → ['가나 ', '다']. */
const tokenize = (paragraph: string): string[] => {
  const tokens: string[] = [];
  let current = '';
  for (const char of paragraph) {
    current += char;
    if (char === ' ') {
      tokens.push(current);
      current = '';
    }
  }
  if (current.length > 0) tokens.push(current);
  return tokens;
};

/** 폭을 넘는 토큰을 글자 단위로 자른다. 한 줄에 최소 한 글자는 둔다. */
const breakChars = (
  chars: readonly string[],
  node: LayoutInput,
  familyName: string,
  measure: MeasureText,
): string[][] => {
  const lines: string[][] = [];
  let current: string[] = [];
  for (const char of chars) {
    const candidate = [...current, char];
    if (current.length > 0 && lineWidthMm(candidate, node, familyName, measure) > node.width) {
      lines.push(current);
      current = [char];
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) lines.push(current);
  return lines;
};

const wrapParagraph = (
  paragraph: string,
  node: LayoutInput,
  familyName: string,
  measure: MeasureText,
): string[][] => {
  if (paragraph.length === 0) return [[]];
  const lines: string[][] = [];
  let current: string[] = [];
  for (const token of tokenize(paragraph)) {
    const chars = [...token];
    const candidate = [...current, ...chars];
    if (lineWidthMm(trimTrailingSpaces(candidate), node, familyName, measure) <= node.width) {
      current = candidate;
      continue;
    }
    if (current.length > 0) lines.push(current);
    const word = trimTrailingSpaces(chars);
    const trailing = chars.slice(word.length);
    const pieces = breakChars(word, node, familyName, measure);
    const last = pieces.pop() ?? [];
    lines.push(...pieces);
    current = [...last, ...trailing];
  }
  lines.push(current);
  return lines;
};

const trimTrailingSpaces = (chars: readonly string[]): string[] => {
  const copy = [...chars];
  while (copy.length > 0 && copy[copy.length - 1] === ' ') copy.pop();
  return copy;
};

const alignX = (align: TextNode['align'], widthMm: number, lineWidth: number): number => {
  if (align === 'center') return (widthMm - lineWidth) / 2;
  if (align === 'right') return widthMm - lineWidth;
  return 0;
};

/**
 * 텍스트 노드의 줄바꿈·정렬·높이를 계산한다(T5). 측정기는 100px 기준 폭을 돌려준다.
 * 줄 높이 = lineHeight × fontSizeMm. 기준선 = 줄 위 + (줄 높이 − 글자 크기)/2 + 0.8 × 글자 크기.
 */
export const layoutText = (
  node: LayoutInput,
  familyName: string,
  measure: MeasureText,
): TextLayout => {
  const lineHeightMm = node.lineHeight * node.fontSizeMm;
  const baselineOffsetMm = (lineHeightMm - node.fontSizeMm) / 2 + TEXT_ASCENT_EM * node.fontSizeMm;
  const wrapped = node.text
    .split('\n')
    .flatMap((paragraph) => wrapParagraph(paragraph, node, familyName, measure));
  const lines = wrapped.map((chars, index) => {
    const visible = trimTrailingSpaces(chars);
    const widthMm = lineWidthMm(visible, node, familyName, measure);
    return {
      text: visible.join(''),
      widthMm,
      xMm: alignX(node.align, node.width, widthMm),
      baselineMm: index * lineHeightMm + baselineOffsetMm,
    };
  });
  return { lines, heightMm: lines.length * lineHeightMm, lineHeightMm };
};
